<template>
  <div class="flex flex-col items-center">
    <h2
      v-if="title"
      class="font-600 text-text-primary dark:text-text-primary-dark text-[20px] self-start"
    >
      {{ title }}
    </h2>

    <!-- Botones arriba -->
    <div
      v-if="buttonsPosition === 'top'"
      class="flex justify-center flex-wrap gap-3 w-full max-w-137.75 p-2 rounded-sm mb-4"
    >
      <CustomButton
        v-for="button in buttons"
        :key="button.id"
        :class="['text-[12px]! h-8!', button.class || 'w-17.5!']"
        :variant="
          currentRange === button.id
            ? button.activeVariant || ''
            : button.inactiveVariant || 'primary_outlined'
        "
        :label="button.label"
        @click="handleButtonClick(button)"
      />
    </div>

    <div ref="chartDiv" :class="chartContainerClass"></div>

    <div
      v-if="buttonsPosition === 'bottom'"
      class="flex justify-center flex-wrap gap-3 w-full max-w-137.75 p-2 rounded-sm"
    >
      <CustomButton
        v-for="button in buttons"
        :key="button.id"
        :class="[
          'text-[12px]! h-8!',
          button.class || 'w-17.5!',
          currentRange === button.id ? '' : 'dark:bg-transparent',
        ]"
        :variant="
          currentRange === button.id
            ? button.activeVariant || ''
            : button.inactiveVariant || 'primary_outlined'
        "
        :label="button.label"
        @click="handleButtonClick(button)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onBeforeUnmount, nextTick } from "vue";
import type * as am5 from "@amcharts/amcharts5";
import type * as am5xy from "@amcharts/amcharts5/xy";
import { useThemeStore } from "../../stores/index";
import { ForexChartService, ChartDataPoint } from "../../index";
// Interfaces para tipado
interface ChartButton {
  id: number | string;
  label: string;
  class?: string;
  activeVariant?: string;
  inactiveVariant?: string;
  onClick?: (button: ChartButton) => void;
  customData?: any;
}

interface ChartData {
  date: number | Date;
  value: number;
}

const props = defineProps({
  // Configuración básica
  clientId: {
    type: [String, Number],
    default: 1,
  },
  title: {
    type: String,
    default: "Gráfico USD/CLP",
  },

  buttons: {
    type: Array as () => ChartButton[],
    default: () => [
      { id: 1, label: "5 días", class: "!w-[70px]" },
      { id: 2, label: "1 mes", class: "!w-[68px]" },
      { id: 3, label: "6 meses", class: "!w-[84px]" },
      { id: 4, label: "YTD", class: "!w-[59px]" },
      { id: 5, label: "1 año", class: "!w-[65px]" },
      { id: 6, label: "Histórico", class: "!w-[80px]" },
    ],
  },
  buttonsPosition: {
    type: String as () => "top" | "bottom" | "none",
    default: "bottom",
    validator: (value: string) => ["top", "bottom", "none"].includes(value),
  },

  // Configuración del gráfico
  chartContainerClass: {
    type: String,
    default: "!w-full !max-w-[551px] !h-[235px] mx-auto",
  },
  chartConfig: {
    type: Object,
    default: () => ({
      strokeWidth: 5,
      strokeColor: "#1D7CFA",
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
    }),
  },

  // Configuración de datos
  useForexService: {
    type: Boolean,
    default: true,
  },
  customDataSource: {
    type: Function,
    default: null,
  },
  staticData: {
    type: Array as () => ChartData[],
    default: () => [],
  },

  // Configuración de tema
  enableDarkTheme: {
    type: Boolean,
    default: true,
  },
});

// Emits para comunicación con el componente padre
const emit = defineEmits<{
  buttonClick: [button: ChartButton, range: number | string];
  dataLoaded: [data: ChartData[]];
  chartCreated: [chart: any];
  error: [error: any];
}>();

const forexChartServices = props.useForexService
  ? new ForexChartService()
  : null;
const chartDiv = ref(null);
const currentRange = ref(props.buttons[0]?.id || 1);
const themeStore = props.enableDarkTheme ? useThemeStore() : null;
const sampleData = ref<ChartData[]>([]);
const isLoading = ref(false);

let root: am5.Root | null = null;
let chart: am5xy.XYChart | null = null;
let series: am5xy.LineSeries | null = null;

const createChart = async () => {
  if (!chartDiv.value) {
    console.error("chartDiv no está disponible");
    return;
  }

  // Cargar amcharts dinámicamente
  let am5,
    am5xy,
    am5themes_Animated,
    am5themes_Dark,
    am5themes_Responsive,
    am5locales_es_ES;
  try {
    [
      am5,
      am5xy,
      am5themes_Animated,
      am5themes_Dark,
      am5themes_Responsive,
      am5locales_es_ES,
    ] = await Promise.all([
      import("@amcharts/amcharts5"),
      import("@amcharts/amcharts5/xy"),
      import("@amcharts/amcharts5/themes/Animated"),
      import("@amcharts/amcharts5/themes/Dark"),
      import("@amcharts/amcharts5/themes/Responsive"),
      import("@amcharts/amcharts5/locales/es_ES"),
    ]);
  } catch (e) {
    console.warn("amcharts5 no está disponible. El gráfico no se cargará.", e);
    return;
  }

  // Limpiar gráfico anterior
  if (root) {
    root.dispose();
    root = null;
  }

  await nextTick();

  root = am5.Root.new(chartDiv.value);

  // Corregir los tipos de temas
  const themes: any[] = [
    (am5themes_Animated.default || am5themes_Animated).new(root),
  ];
  if (props.enableDarkTheme && themeStore?.currentTheme === "dark") {
    themes.push((am5themes_Dark.default || am5themes_Dark).new(root));
  }
  themes.push((am5themes_Responsive.default || am5themes_Responsive).new(root));
  root.setThemes(themes);

  root._logo?.dispose();
  root.locale = am5locales_es_ES.default || am5locales_es_ES;

  chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: props.chartConfig.panX,
      panY: props.chartConfig.panY,
      wheelX: props.chartConfig.wheelX,
      wheelY: props.chartConfig.wheelY,
      layout: root.verticalLayout,
    }),
  );

  const cursor = chart.set(
    "cursor",
    am5xy.XYCursor.new(root, {
      behavior: "none",
    }),
  );
  cursor.lineY.set("visible", false);

  const xAxis = chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      maxDeviation: 0,
      baseInterval: { timeUnit: "day", count: 1 },
      renderer: am5xy.AxisRendererX.new(root, {}),
      tooltip: am5.Tooltip.new(root, {}),
    }),
  );

  const yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, {}),
    }),
  );
  yAxis.setAll({
    extraMin: 0,
  });
  xAxis.get("renderer").grid.template.set("visible", false);

  series = chart.series.push(
    am5xy.LineSeries.new(root, {
      name: "Precio",
      xAxis,
      yAxis,
      valueYField: "value",
      valueXField: "date",
      stroke: am5.color(props.chartConfig.strokeColor),
      tooltip: am5.Tooltip.new(root, {
        labelText: "{valueY}",
        pointerOrientation: "horizontal",
      }),
    }),
  );

  series.strokes.template.setAll({
    strokeWidth: props.chartConfig.strokeWidth,
  });

  if (sampleData.value.length > 0) {
    series.data.setAll(sampleData.value);
  }

  emit("chartCreated", chart);
};

const updateData = async (range: number | string) => {
  if (isLoading.value) return;

  currentRange.value = range;
  isLoading.value = true;

  try {
    let chartData: ChartData[] = [];

    if (props.staticData.length > 0) {
      // Usar datos estáticos
      chartData = props.staticData;
    } else if (props.customDataSource) {
      chartData = await props.customDataSource(props.clientId, range);
    } else if (props.useForexService && forexChartServices) {
      const clientId = parseInt(props.clientId.toString());
      const response = await forexChartServices.getForexChartData(
        clientId,
        Number(range),
      );
      if (response && response.data && Array.isArray(response.data)) {
        const chartDataPoint = response.data.find(
          (item: ChartDataPoint) =>
            item.clientId === clientId && item.range.id === range,
        );
        if (chartDataPoint && chartDataPoint.data) {
          chartData = chartDataPoint.data.map((item: any) => ({
            date: new Date(item.date).getTime(),
            value: parseFloat(item.value),
          }));
        }
      }
    }
    const formattedData = chartData.map((item) => ({
      date:
        typeof item.date === "number"
          ? item.date
          : new Date(item.date).getTime(),
      value:
        typeof item.value === "number"
          ? item.value
          : parseFloat(String(item.value)),
    }));

    sampleData.value = formattedData;
    emit("dataLoaded", formattedData);

    if (series) {
      series.data.setAll(formattedData);
    } else {
      await createChart();
    }
  } catch (error) {
    emit("error", error);
    sampleData.value = [];
    if (series) {
      series.data.setAll([]);
    }
  } finally {
    isLoading.value = false;
  }
};

const handleButtonClick = async (button: ChartButton) => {
  if (button.onClick) {
    button.onClick(button);
  }

  emit("buttonClick", button, button.id);

  await updateData(button.id);
};

watch(
  () => props.clientId,
  async () => {
    await updateData(currentRange.value);
  },
);

watch(
  () => props.staticData,
  async () => {
    if (props.staticData.length > 0) {
      await updateData(currentRange.value);
    }
  },
  { deep: true },
);

if (props.enableDarkTheme && themeStore) {
  watch(
    () => themeStore.currentTheme,
    async () => {
      await createChart();
    },
  );
}

onMounted(async () => {
  await nextTick();
  if (props.staticData.length > 0 || !props.useForexService) {
    await createChart();
    if (props.staticData.length > 0) {
      sampleData.value = props.staticData;
      if (series) {
        series.data.setAll(props.staticData);
      }
    }
  } else {
    await updateData(currentRange.value);
  }
});

onBeforeUnmount(() => {
  if (root) {
    root.dispose();
    root = null;
  }
});
</script>
